import PopoverComponent from '@/components_v2/PopoverComponent';
import { Box, Flex, Text } from '@chakra-ui/react';
import { flexRender } from '@tanstack/react-table';
import { isEmpty } from 'lodash';
import React from 'react';
import {
  AiOutlineSortAscending,
  AiOutlineSortDescending,
} from 'react-icons/ai';

import {
  ColumDef,
  ColumnProps,
  CustomHeaderGroup,
} from '../../../../interfaces';
import { useTabsViewsContext } from '../../../TabsViews';
import { Filters } from './components/Filters';
import { CellHeader, FilterContainer, ToolTip } from './styles';

const DEFAULT_HEADER_STYLE = {
  sortable: false,
  textAlign: 'center',
  backgroundColor: 'transparent',
  borderColor: 'transparent',
  headerTextWidth: '100%',
  textColor: 'black',
  flex: 1,
  filterTitle: null,
};

const Column: React.FC<ColumnProps> = ({
  headerGroup,
  headerBackgroundColor,
  headerBorderColor,
  headerTextColor,
  headerTextWeight,
  restColumns,
  headerFontSize,
  rowHeight,
  columnsFilters,
}) => {
  const { hasTabs, activeTabs, tabsViewsMap } = useTabsViewsContext();

  return (
    <tr style={{ display: 'flex' }}>
      {headerGroup.headers.map((column: CustomHeaderGroup, index: number) => {
        const { id, headerTooltip, originalId, isPlaceholder } = column;

        const {
          getToggleSortingHandler,
          getCanSort,
          getIsSorted,
          getCanFilter,
        } = column?.column;

        const canFilter = getCanFilter();
        const sortable = getCanSort();
        const isSorted = getIsSorted();

        const {
          columnHeaderBackgroundColor,
          minWidth,
          width,
          accessorKey,
        } = column?.column?.columnDef as ColumDef;

        const header = flexRender(
          column.column.columnDef.header,
          column.getContext(),
        );

        const {
          textAlign,
          backgroundColor,
          textColor,
          borderColor,
          headerTextWidth,
          flex = 1,
          filterTitle,
        } =
          restColumns[accessorKey || originalId || id] ?? DEFAULT_HEADER_STYLE;

        const columnFilter =
          columnsFilters && !isEmpty(columnsFilters)
            ? columnsFilters[id]
            : {
                type: null,
                values: [],
              };

        const findActiveTab = Array.from(activeTabs?.values())?.find((item) =>
          item?.selected.find(
            (selected) =>
              column?.originalId?.includes(selected?.for_column) ||
              selected?.for_column === id,
          ),
        );

        const valueForColumn = findActiveTab?.selected.find(
          (tab) => tab.for_column === id.replace(/_placeholder(_\d+)?$/, ''),
        );

        const teste =
          valueForColumn?.for_column === id.replace(/_placeholder(_\d+)?$/, '');

        if (
          hasTabs &&
          !activeTabs.get(id.replace(/_placeholder(_\d+)?$/, ''))?.hasValue &&
          !teste &&
          tabsViewsMap.get(id.replace(/_placeholder(_\d+)?$/, ''))
        ) {
          return <React.Fragment key={id} />;
        }

        if (column?.originalId === 'expander_placeholder') {
          return <React.Fragment key="expander_placeholder" />;
        }

        const activeTabsSize = Array.from(activeTabs?.values())?.filter(
          (item) =>
            item?.selected?.filter((sel) => sel?.label !== item?.label)
              ?.length > 0,
        )?.length;

        const flexSize = activeTabsSize + 1;

        const groupFlex =
          accessorKey?.includes('group') && hasTabs && index === 0
            ? flexSize
            : flex;

        const renderCellHead = () => (
          <CellHeader
            isSorted={isSorted}
            width={!isPlaceholder ? width : undefined}
            minWidth={minWidth || 0}
            textAlign={textAlign}
            headerFontSize={headerFontSize}
            headerBackgroundColor={
              columnHeaderBackgroundColor ||
              backgroundColor ||
              headerBackgroundColor ||
              'transparent'
            }
            headerBorderColor={
              borderColor || headerBorderColor || 'transparent'
            }
            headerTextWeight={headerTextWeight}
            headerTextColor={textColor || headerTextColor || '#7d7d7d'}
            height={rowHeight || 35}
            flex={
              (id !== 'expander' && !width) || isPlaceholder
                ? groupFlex
                : undefined
            }
          >
            {isPlaceholder ? null : (
              <Flex
                position="relative"
                height="100%"
                alignItems="center"
                justifyContent="center"
                margin="auto"
                padding={0}
                onClick={getToggleSortingHandler()}
              >
                {sortable && isSorted && (
                  <Box flex={0.3} position="absolute" left={0}>
                    {isSorted === 'desc' ? (
                      <AiOutlineSortDescending size={14} />
                    ) : (
                      <AiOutlineSortAscending size={14} />
                    )}
                  </Box>
                )}
                <Box
                  style={
                    (id !== 'expander' && !width) || isPlaceholder
                      ? {
                          flex,
                          cursor: isPlaceholder ? 'default' : 'pointer',
                        }
                      : {}
                  }
                >
                  {header && (
                    <Text
                      display="flex"
                      width="100%"
                      height="100%"
                      alignItems="center"
                      justifyContent="center"
                      maxWidth={headerTextWidth ?? 'auto'}
                      margin="auto"
                      overflow="hidden"
                      noOfLines={2}
                      textOverflow="ellipsis"
                      lineHeight={1}
                    >
                      {header}
                    </Text>
                  )}
                </Box>
                {!isPlaceholder && canFilter && (
                  <FilterContainer
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    <Filters
                      column={column}
                      style={{
                        color: textColor || headerTextColor || '#7d7d7d',
                      }}
                      {...columnFilter}
                      title={filterTitle as string}
                    />
                  </FilterContainer>
                )}
              </Flex>
            )}
          </CellHeader>
        );

        const headerText = typeof header === 'string' ? header : '';

        return headerText.length < 3 ? (
          renderCellHead()
        ) : (
          <PopoverComponent placement="top" trigger={renderCellHead()}>
            {header || headerTooltip ? (
              <ToolTip>{headerTooltip || header}</ToolTip>
            ) : null}
          </PopoverComponent>
        );
      })}
    </tr>
  );
};

export default Column;
